import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import Storage, { useStorageValue } from '@utils/storage.ts'
import { ProductMin, Subscription, UiInlineAlert } from '@src/types.ts'
import { usePaidCallCreditCount } from '@src/hooks/usePaidCallCreditCount.ts'
import { Feature, useFeatureFlag } from '@components/FeatureFlag'
import { userSelector } from '@selectors/app.ts'
import { IconName } from '@components/base'

export const useNoMorePaidCallAlert = (
  videoCallSubscription?: Pick<Subscription, 'currentPeriodEndAt'>,
  videoCallSubscriptionProduct?: Pick<ProductMin, 'id' | 'recurring'> | null,
) => {
  const navigation = useNavigation()
  const user = useSelector(userSelector)
  const paidCallCreditCount = usePaidCallCreditCount()
  const videoCallCreditsEnabled = useFeatureFlag(Feature.VideoCallCredits)
  const noMorePaidCallAlertClosedKey = `${Storage.NO_MORE_PAID_CALLS_ALERT_CLOSED_KEY}_${videoCallSubscriptionProduct?.id}`
  const [noMorePaidCallAlertClosed, setNoMorePaidCallAlertClosed] = useStorageValue(
    noMorePaidCallAlertClosedKey,
  )

  const onClose = useCallback(() => {
    if (videoCallSubscriptionProduct) {
      setNoMorePaidCallAlertClosed(true)
    }
  }, [setNoMorePaidCallAlertClosed, videoCallSubscriptionProduct])

  const showNoMorePaidCallAlert =
    videoCallCreditsEnabled &&
    !noMorePaidCallAlertClosed &&
    videoCallSubscription &&
    videoCallSubscriptionProduct &&
    user?.uhcMember &&
    paidCallCreditCount === 0

  const noMorePaidCallAlertProps = useMemo((): {
    message: string
    alertCategory: UiInlineAlert
    rightIconName: IconName
    onRightIconPress: () => void
    onLeftIconPress?: () => void
    leftIconName?: IconName
    leftIconAccessibilityLabel?: string
  } | null => {
    if (!showNoMorePaidCallAlert) {
      return null
    }
    const message = videoCallSubscriptionProduct.recurring
      ? 'Buy more calls in the UHC Store or wait for next month'
      : 'Go back to the UHC Store to get more and stay on track!'

    return {
      message,
      alertCategory: UiInlineAlert.Warning,
      rightIconName: 'caret-right',
      onRightIconPress: () => {
        navigation.navigate('NoMoreCallsLeftModal', {
          nextBillingDate: videoCallSubscription.currentPeriodEndAt,
          recurring: videoCallSubscriptionProduct.recurring,
        })
      },
      leftIconName: 'x',
      onLeftIconPress: onClose,
      leftIconAccessibilityLabel: 'close',
    }
  }, [
    navigation,
    onClose,
    showNoMorePaidCallAlert,
    videoCallSubscription,
    videoCallSubscriptionProduct,
  ])

  return { showNoMorePaidCallAlert, noMorePaidCallAlertProps }
}
